import type { ScheduleResource, ScheduleVehicle } from '../../../services/scheduleService';
import { taskMatchesSchedulePlannedDateFilters } from './schedulePlannedDateWindow';
import { taskStatusMatchesScheduleSelection } from './scheduleTaskStatuses';

/**
 * Schedule filters (aligned with ResourceGanttChart / SchedulePage).
 *
 * Every dropdown-backed field is a multiselect: an empty array always means "no filter". The
 * plural names are deliberate — they were scalars before, and renaming forces the compiler to
 * surface every read site instead of letting an array silently flow into a `===` comparison.
 */
export interface ScheduleResourcePageFilters {
  /** Empty = all service areas; values are service-area ids (numeric strings) or names. */
  serviceAreas: string[];
  /** Empty = all foreman areas; values are foreman-area ids (numeric strings) or names. */
  foremanAreas: string[];
  /** Empty = all equipment types; otherwise the task's mapped vehicle type must be one of these. */
  equipmentTypes: string[];
  /** Empty = all projects; otherwise task must match one of these project ids. */
  projectIds: number[];
  /** Empty = all activities; otherwise task must match one of these activity ids. */
  activityIds: number[];
  /**
   * Activity numbers from the selected dropdown rows (normalized trim). When task links use a
   * different Activity row id than the list (same number, duplicate costing rows), matching by
   * number still includes the task.
   */
  activityNumbers: string[];
  /** Empty = all statuses; otherwise task.status must be one of these values. */
  statuses: string[];
  startDate: string;
  endDate: string;
  /** Empty = all work types; otherwise task.type must be one of these ("All" is normalized away). */
  workTypes: string[];
  taskSearch: string;
}

/** Trimmed, de-duplicated, non-empty values from a multiselect filter. */
export function normalizeScheduleFilterValues(
  values: Array<string | number> | undefined | null
): string[] {
  const list = Array.isArray(values) ? values : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const v = String(raw ?? '').trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/**
 * Wire format for multiselect params: a comma-separated list, or `undefined` when nothing is
 * selected (so "no filter" keeps meaning "param absent"). The server splits on commas and also
 * accepts a single bare value, so old links keep working.
 */
export function toScheduleListParam(
  values: Array<string | number> | undefined | null
): string | undefined {
  const normalized = normalizeScheduleFilterValues(values);
  return normalized.length > 0 ? normalized.join(',') : undefined;
}

export function hashStringToNumber(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/** Parsed schedule filter: numeric service area id from dropdown, or null when "All". */
export function parseServiceAreaFilterId(serviceAreaParam: string): number | null {
  const s = (serviceAreaParam || '').trim();
  if (!s || !/^\d+$/.test(s)) return null;
  const id = parseInt(s, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Numeric service-area ids selected in the multiselect. Empty array = "All" (no restriction).
 * Non-numeric entries (names) are dropped: the client-side aggregation only knows ids.
 */
export function parseServiceAreaFilterIds(serviceAreaParams: string[] | undefined | null): number[] {
  const out: number[] = [];
  for (const raw of normalizeScheduleFilterValues(serviceAreaParams)) {
    const id = parseServiceAreaFilterId(raw);
    if (id != null && !out.includes(id)) out.push(id);
  }
  return out;
}

/** `null` when unrestricted, otherwise the set of allowed service-area ids. */
function toServiceAreaIdSet(filter: number | number[] | null | undefined): Set<number> | null {
  if (filter == null) return null;
  const ids = (Array.isArray(filter) ? filter : [filter]).filter((n) => Number.isFinite(n) && n > 0);
  return ids.length > 0 ? new Set(ids) : null;
}

/** True when filter string and task activity number refer to the same code (exact or numeric). */
export function scheduleActivityNumberMatches(filterValue: string, taskActivityNumber: string | null | undefined): boolean {
  const a = String(filterValue ?? '').trim();
  const b = String(taskActivityNumber ?? '').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const na = parseInt(a, 10);
  const nb = parseInt(b, 10);
  return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
}

export function taskMatchesScheduleActivityFilter(
  task: {
    activities?: Array<{ id: number; activityNumber?: string | null }> | null;
    /** Often equals activity number; used when `activities` is missing on the payload. */
    taskNumber?: string | null;
  },
  filters: Pick<ScheduleResourcePageFilters, 'activityIds' | 'activityNumbers'>
): boolean {
  const activityIds = (filters.activityIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  const activityNumbers = (filters.activityNumbers || []).map((n) => String(n).trim()).filter((n) => n.length > 0);
  if (activityIds.length === 0 && activityNumbers.length === 0) return true;
  const list = Array.isArray(task.activities) ? task.activities : [];
  const idHit = activityIds.length > 0 && list.some((act) => activityIds.includes(Number(act.id)));
  const numHit =
    activityNumbers.length > 0 &&
    list.some((act) =>
      activityNumbers.some((sel) => scheduleActivityNumberMatches(sel, act.activityNumber))
    );
  if (idHit || numHit) return true;
  const taskNo = String(task.taskNumber ?? '').trim();
  if (activityNumbers.length > 0 && taskNo) {
    return activityNumbers.some((sel) => scheduleActivityNumberMatches(sel, taskNo));
  }
  return false;
}

export function filterResourcesByTaskFilters(
  resources: ScheduleResource[],
  vehicleMeta: ScheduleVehicle[] | null,
  filters: ScheduleResourcePageFilters
): ScheduleResource[] {
  const projectIds = (Array.isArray(filters.projectIds) ? filters.projectIds : []).filter(
    (id) => Number.isFinite(id) && id > 0
  );
  const hasProjectFilter = projectIds.length > 0;

  const activityIds = (Array.isArray(filters.activityIds) ? filters.activityIds : []).filter(
    (id) => Number.isFinite(id) && id > 0
  );
  const activityNumbers = (Array.isArray(filters.activityNumbers) ? filters.activityNumbers : [])
    .map((n) => String(n || '').trim())
    .filter((n) => n.length > 0);
  const hasActivityFilter = activityIds.length > 0 || activityNumbers.length > 0;

  const statusFilter = (Array.isArray(filters.statuses) ? filters.statuses : [])
    .map((s) => String(s || '').trim())
    .filter((s) => s.length > 0);
  const hasStatusFilter = statusFilter.length > 0;

  const equipmentTypeFilter = new Set(
    normalizeScheduleFilterValues(filters.equipmentTypes).map((t) => t.toLowerCase())
  );
  const hasEquipmentTypeFilter = equipmentTypeFilter.size > 0;
  const hasPlannedDateFilter =
    !!(filters.startDate || '').trim() || !!(filters.endDate || '').trim();

  if (
    !hasProjectFilter &&
    !hasActivityFilter &&
    !hasEquipmentTypeFilter &&
    !hasStatusFilter &&
    !hasPlannedDateFilter
  ) {
    return resources;
  }

  const unitToType = new Map<string, string>();
  (Array.isArray(vehicleMeta) ? vehicleMeta : []).forEach((v) => {
    const unit = (v.unitNumber || '').trim();
    if (!unit) return;
    const type = (v.vehicleType || 'Unspecified').trim() || 'Unspecified';
    unitToType.set(unit, type);
  });

  return (Array.isArray(resources) ? resources : [])
    .map((r) => {
      const tasks = (Array.isArray(r.tasks) ? r.tasks : []).filter((t) => {
        if (hasProjectFilter && (!t.projectId || !projectIds.includes(t.projectId))) return false;

        if (hasActivityFilter && !taskMatchesScheduleActivityFilter(t, { activityIds, activityNumbers })) {
          return false;
        }

        if (hasEquipmentTypeFilter) {
          const matchesType = (Array.isArray(t.vehicles) ? t.vehicles : []).some((v) => {
            const unit = (v.unitUsed || '').trim();
            if (!unit) return false;
            const mapped = (unitToType.get(unit) || 'Unspecified').toLowerCase();
            return equipmentTypeFilter.has(mapped);
          });
          if (!matchesType) return false;
        }

        if (hasStatusFilter) {
          const st = String(t.status ?? '').trim();
          if (!taskStatusMatchesScheduleSelection(st, statusFilter)) return false;
        }

        if (hasPlannedDateFilter && !taskMatchesSchedulePlannedDateFilters(t, filters.startDate, filters.endDate)) {
          return false;
        }
        return true;
      });
      return { ...r, tasks };
    })
    .filter((r) => (Array.isArray(r.tasks) ? r.tasks : []).length > 0);
}

/** Client-side task search (resources API does not filter by task text). */
export function filterResourcesByTaskSearch(resources: ScheduleResource[], taskSearch: string): ScheduleResource[] {
  const needle = String(taskSearch || '').trim().toLowerCase();
  if (!needle) return resources;
  return (Array.isArray(resources) ? resources : [])
    .map((r) => {
      const tasks = (Array.isArray(r.tasks) ? r.tasks : []).filter((t) => {
        const num = String(t.taskNumber ?? '').toLowerCase();
        const name = String(t.name ?? '').toLowerCase();
        const desc = String(t.description ?? '').toLowerCase();
        return num.includes(needle) || name.includes(needle) || desc.includes(needle);
      });
      return { ...r, tasks };
    })
    .filter((r) => (Array.isArray(r.tasks) ? r.tasks : []).length > 0);
}

/**
 * @param serviceAreaFilter `null`/empty = all service areas. A single id is still accepted so
 *   pre-multiselect callers keep working.
 */
export function buildVehicleResources(
  employeeResources: ScheduleResource[],
  vehicleMeta: ScheduleVehicle[] | null,
  serviceAreaFilter: number | number[] | null
): ScheduleResource[] {
  const allowedServiceAreaIds = toServiceAreaIdSet(serviceAreaFilter);
  const unitToType = new Map<string, string>();
  const capacityByServiceAreaAndType = new Map<string, number>();

  if (Array.isArray(vehicleMeta)) {
    for (const v of vehicleMeta) {
      const typeKey = (v.vehicleType || 'Unspecified').trim() || 'Unspecified';
      const unit = (v.unitNumber || '').trim();
      if (unit) unitToType.set(unit, typeKey);
      const sas = Array.isArray(v.serviceAreas) ? v.serviceAreas : [];
      for (const sa of sas) {
        if (allowedServiceAreaIds && !allowedServiceAreaIds.has(sa.id)) continue;
        const k = `${sa.id}|${typeKey}`;
        capacityByServiceAreaAndType.set(k, (capacityByServiceAreaAndType.get(k) || 0) + 1);
      }
    }
  }

  const byTypeAndServiceArea = new Map<
    string,
    {
      serviceArea: ScheduleResource['serviceArea'];
      typeKey: string;
      capacity: number;
      tasksById: Map<number, ScheduleResource['tasks'][number]>;
    }
  >();

  for (const employee of employeeResources) {
    const serviceArea = employee.serviceArea;
    const serviceAreaId = serviceArea?.id ?? 0;
    if (allowedServiceAreaIds && !allowedServiceAreaIds.has(serviceAreaId)) continue;

    const tasks = Array.isArray(employee.tasks) ? employee.tasks : [];

    for (const task of tasks) {
      const vehicles = Array.isArray(task.vehicles) ? task.vehicles : [];
      const mappedTypes = vehicles
        .map((vv) => (vv.unitUsed || '').trim())
        .filter((u) => u.length > 0)
        .map((u) => unitToType.get(u) || 'Unspecified');
      const typeSet = new Set(mappedTypes.length > 0 ? mappedTypes : ['Unspecified']);

      for (const typeKey of Array.from(typeSet)) {
        const key = `${serviceAreaId}|${typeKey}`;
        const capacity = capacityByServiceAreaAndType.get(key) || 0;
        const entry =
          byTypeAndServiceArea.get(key) || {
            serviceArea: serviceArea ?? null,
            typeKey,
            capacity,
            tasksById: new Map<number, ScheduleResource['tasks'][number]>(),
          };
        entry.tasksById.set(task.id, task);
        byTypeAndServiceArea.set(key, entry);
      }
    }
  }

  // Also include available type capacity rows even if no tasks are planned.
  capacityByServiceAreaAndType.forEach((capacity, key) => {
    const [saIdRaw, typeKey] = key.split('|');
    const saId = Number(saIdRaw);
    const existing = byTypeAndServiceArea.get(key);
    if (existing) {
      existing.capacity = capacity;
      return;
    }
    byTypeAndServiceArea.set(key, {
      serviceArea: Number.isFinite(saId) && saId > 0 ? { id: saId, name: `Service Area ${saId}` } : null,
      typeKey: typeKey || 'Unspecified',
      capacity,
      tasksById: new Map<number, ScheduleResource['tasks'][number]>(),
    });
  });

  return Array.from(byTypeAndServiceArea.entries())
    .map(([key, entry]) => ({
      id: hashStringToNumber(key),
      name: entry.typeKey,
      lastName: '',
      roleName: null,
      foremanArea: null,
      serviceArea: entry.serviceArea,
      position: `${entry.serviceArea?.name ?? 'Unassigned area'} | capacity ${entry.capacity}`,
      tasks: Array.from(entry.tasksById.values()),
    }))
    /** Equipment chart: hide vehicle-type rows with no scheduled tasks (omit capacity-only placeholders). */
    .filter((r) => (Array.isArray(r.tasks) ? r.tasks : []).length > 0);
}
